import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { base44 } from '@/api/base44Client';
import { UI_STRINGS } from '@/lib/uiStrings';
import { getPublicLists } from '@/lib/publicLists';
import { LEGAL_STRINGS } from '@/lib/legalStrings';
import { isBot } from '@/lib/isBot';
import { afterHuman } from '@/lib/humanGate';
import { requestTopic } from '@/lib/topicTranslateQueue';

// Everything the interface can show, including the legal and policy pages.
const ALL_STRINGS = { ...LEGAL_STRINGS, ...UI_STRINGS };

const TranslationContext = createContext({ t: (k) => UI_STRINGS[k] || k, tTopic: (n) => n });

const cache = {};
const topicCache = {};
const topicsRequested = {};

export function TranslationProvider({ currentLang, children }) {
  const [strings, setStrings] = useState(cache[currentLang] || null);

  useEffect(() => {
    if (!currentLang || currentLang === 'en') { setStrings(null); return; }
    let cancelled = false;
    (async () => {
      if (cache[currentLang]) {
        setStrings(cache[currentLang]);
      } else {
        try {
          const { strings: loaded } = await getPublicLists(currentLang);
          if (loaded) {
            cache[currentLang] = loaded;
            if (!cancelled) setStrings(loaded);
          }
        } catch (e) { /* ignore */ }
      }
      // Topics are created on the fly, so fill in any newly added topic names once per session.
      // Crawlers never trigger translation work.
      if (isBot || topicsRequested[currentLang]) return;
      topicsRequested[currentLang] = true;
      await new Promise(resolve => afterHuman(resolve));
      if (cancelled) { topicsRequested[currentLang] = false; return; }
      try {
        const res = await base44.functions.invoke('translateTopics', { language: currentLang, ui_strings: ALL_STRINGS });
        if (res.data?.strings) {
          cache[currentLang] = { ...(cache[currentLang] || {}), ...res.data.strings };
          if (!cancelled) setStrings(cache[currentLang]);
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [currentLang]);

  const t = useCallback((key, vars) => {
    let str = (strings && strings[key]) || ALL_STRINGS[key] || key;
    if (vars) {
      Object.keys(vars).forEach(k => {
        str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), vars[k]);
      });
    }
    return str;
  }, [strings]);

  // Topic names live in the database (English) — translate via cached strings, fall back to the original name
  const [topicNames, setTopicNames] = useState({});
  const onTopics = useCallback((lang, items) => {
    if (!Object.keys(items).length) return;
    topicCache[lang] = { ...(topicCache[lang] || {}), ...items };
    setTopicNames(prev => ({ ...prev, [lang]: topicCache[lang] }));
  }, []);

  // A topic shown in a language it isn't translated into yet is asked for on demand,
  // saved once, and served to every reader in that language after.
  const tTopic = useCallback((name) => {
    if (!name || !currentLang || currentLang === 'en') return name;
    const hit = topicNames[currentLang]?.[name] || topicCache[currentLang]?.[name] || (strings && strings[`topic.${name}`]);
    if (hit) return hit;
    requestTopic(currentLang, [name], onTopics);
    return name;
  }, [strings, topicNames, currentLang, onTopics]);

  return (
    <TranslationContext.Provider value={{ t, tTopic }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  return useContext(TranslationContext);
}